/**
 * Toucan People — the hash routes. Add/edit, review, the tag editor, the
 * import and Settings are pages rendered in `main`; small transient things
 * (the emoji picker, the menu, the lightbox) stay as modal dialogs with no
 * route.
 */

import { goToPath, hashSegments } from "toucan";
import { store } from "./state.ts";
import type { SessionKind } from "./session.ts";

// The platform provides the hash, and this app reads its routes off it.
export { goToPath as go };

/** Every page the hash can name. */
export type Route =
  | { name: "list" }
  | { name: "tag"; id: string }
  | { name: "add" }
  | { name: "edit"; id: string }
  | { name: "review"; kind: SessionKind; tagId?: string }
  | { name: "tags" }
  | { name: "import" }
  | { name: "settings" };

/** The routes that are the browse list: everybody, or one tag's people. */
export type ListRoute = Extract<Route, { name: "list" | "tag" }>;

export function currentRoute(): Route {
  const [name, id] = hashSegments();
  if (name === "edit" && id !== undefined) {
    return { name: "edit", id };
  }
  if (name === "tag" && id !== undefined) {
    return { name: "tag", id };
  }
  if (name === "add") {
    return { name: "add" };
  }
  if (name === "learn" || name === "revise") {
    return id === undefined
      ? { name: "review", kind: name }
      : { name: "review", kind: name, tagId: id };
  }
  if (name === "tags") {
    return { name: "tags" };
  }
  if (name === "import") {
    return { name: "import" };
  }
  if (name === "settings") {
    return { name: "settings" };
  }
  return { name: "list" };
}

/** Whose page this is, for the one route that names a person. */
export function routePersonId(route: Route): string | undefined {
  return route.name === "edit" ? route.id : undefined;
}

/** Which tag the list is narrowed to, or undefined for everybody. */
export function routeTagId(route: Route): string | undefined {
  return route.name === "tag" ? route.id : undefined;
}

/** Whether a route is one of the browse list's own. */
export function isListRoute(route: Route): route is ListRoute {
  return route.name === "list" || route.name === "tag";
}

/** The hash a tag filter is addressed by, with no tag for the whole list. */
export function tagPath(tagId: string | undefined): string {
  return tagId === undefined ? "" : `tag/${tagId}`;
}

/**
 * The hash a review session is addressed by, which names Learn or Revise and
 * says whether it is over one tag's people or over everybody. Both are in the
 * address rather than remembered, so a session reloaded or linked to is the
 * session it was.
 */
export function reviewPath(
  kind: SessionKind,
  tagId: string | undefined,
): string {
  return tagId === undefined ? kind : `${kind}/${tagId}`;
}

/**
 * The tag a review session is over, or undefined for everybody — which a tag
 * that has gone, deleted here or synced away, is too.
 */
export function reviewTagId(route: Route): string | undefined {
  if (route.name !== "review" || route.tagId === undefined) {
    return undefined;
  }
  return store().tags.some((tag) => tag.id === route.tagId)
    ? route.tagId
    : undefined;
}

/**
 * The browse list last shown. A sub-page opened by a link rather than from the
 * list has never seen one, so it stays everybody.
 */
let listLeft: ListRoute = { name: "list" };

/** Note a browse list as the one to come back to, if this route is one. */
export function noteListRoute(route: Route): void {
  if (isListRoute(route)) {
    listLeft = route;
  }
}

/** Where leaving a sub-page goes, the list it was opened from, tag and all. */
export function listPath(): string {
  return tagPath(listTagId());
}

/**
 * The tag the list was last narrowed to, or undefined for everybody. It holds
 * on the pages opened from the list, which is what lets the capture form
 * prefill it.
 */
export function listTagId(): string | undefined {
  return routeTagId(listLeft);
}

/**
 * Forget a tag as the view to come back to — for one that has just been
 * deleted, which {@link listPath} would otherwise lead to.
 */
export function forgetListTag(tagId: string): void {
  if (listLeft.name === "tag" && listLeft.id === tagId) {
    listLeft = { name: "list" };
  }
}

export function personById(id: string) {
  return store().people.find((entry) => entry.id === id);
}
